import { type ActionType, createReducer } from "typesafe-actions";

import crmEnrichmentFiltersService from "../services/filters";

import * as ac from "./action-creators";
import { INITIAL_CRM_ENRICHMENT_STATE, INITIAL_WIZARD_FILTERS } from "./initial-state";
import type { CrmEnrichmentState } from "./types";

const getNextFilterId = (filters: readonly { id: number }[]): number => {
    if (filters.length === 0) {
        return 1;
    }

    return Math.max(...filters.map((f) => f.id)) + 1;
};

const crmEnrichmentReducer = createReducer<CrmEnrichmentState, ActionType<typeof ac>>(INITIAL_CRM_ENRICHMENT_STATE)
    .handleAction(
        ac.setRemoveModalOpen,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            isRemoveModalOpen: payload,
        }),
    )
    .handleAction(
        ac.setRenameModalOpen,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            isRenameModalOpen: payload,
        }),
    )
    .handleAction(
        ac.setJobName,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            jobName: payload,
        }),
    )
    .handleAction(
        ac.resetNameModalState,
        (state): CrmEnrichmentState => ({
            ...state,
            isRenameModalOpen: INITIAL_CRM_ENRICHMENT_STATE.isRemoveModalOpen,
            jobName: INITIAL_CRM_ENRICHMENT_STATE.jobName,
        }),
    )
    .handleAction(
        ac.setSelectedJobType,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            selectedJobType: payload,
        }),
    )
    .handleAction(
        ac.setSelectedJobObject,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            selectedJobObject: payload,
        }),
    )
    .handleAction(
        ac.setSelectedJobRefreshPeriod,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            selectedJobRefreshPeriod: payload,
        }),
    )
    .handleAction(
        ac.setSelectedCountryId,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            selectedCountryId: payload,
        }),
    )
    .handleAction(
        ac.setSelectedEnrichmentSource,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            selectedEnrichmentSource: payload,
        }),
    )
    .handleAction(
        ac.setSelectedLeadScoreRefreshDataInterval,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            selectedLeadScoreRefreshDataInterval: payload,
        }),
    )
    .handleAction(
        ac.setJobState,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            jobName: payload.name,
            selectedJobType: payload.type,
            selectedJobObject: payload.enrichmentObject,
            selectedJobRefreshPeriod: payload.refreshDataInterval,
            selectedLeadScoreRefreshDataInterval: payload.leadScoreRefreshDataInterval ?? "weekly",
            selectedCountryId: payload.country,
            selectedEnrichmentSource: payload.enrichmentSource,
            mappings: payload.mappings ?? [],
        }),
    )
    .handleAction(
        ac.resetJobState,
        (state): CrmEnrichmentState => ({
            ...state,
            ...INITIAL_CRM_ENRICHMENT_STATE,
            wizardFilters: INITIAL_WIZARD_FILTERS,
        }),
    )
    // Wizard Filters Reducers
    .handleAction(
        ac.setIsApplyFiltersSelected,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            wizardFilters: {
                ...state.wizardFilters,
                isApplyFiltersSelected: payload,
            },
        }),
    )
    .handleAction(ac.addFilter, (state): CrmEnrichmentState => {
        const nextId = getNextFilterId(state.wizardFilters.filters);

        return {
            ...state,
            wizardFilters: {
                ...state.wizardFilters,
                filters: [
                    ...state.wizardFilters.filters,
                    { ...crmEnrichmentFiltersService.getDefaultFilter(), id: nextId },
                ],
            },
        };
    })
    .handleAction(
        ac.deleteFilter,
        (state, { payload: id }): CrmEnrichmentState => ({
            ...state,
            wizardFilters: {
                ...state.wizardFilters,
                filters: state.wizardFilters.filters.filter((filter) => filter.id !== id),
            },
        }),
    )
    .handleAction(
        ac.setFilterField,
        (state, { payload: { id, field, dataType, pickListItems } }): CrmEnrichmentState => ({
            ...state,
            wizardFilters: {
                ...state.wizardFilters,
                filters: state.wizardFilters.filters.map((filter) => {
                    if (filter.id !== id) {
                        return filter;
                    }

                    const defaultFilter = crmEnrichmentFiltersService.getDefaultFilter();

                    return {
                        ...defaultFilter,
                        id,
                        field,
                        dataType,
                        pickListItems,
                    };
                }),
            },
        }),
    )
    .handleAction(
        ac.setFilterValue,
        (state, { payload: { id, key, value } }): CrmEnrichmentState => ({
            ...state,
            wizardFilters: {
                ...state.wizardFilters,
                filters: state.wizardFilters.filters.map((filter) =>
                    filter.id === id ? { ...filter, [key]: value } : filter,
                ),
            },
        }),
    )
    .handleAction(
        ac.resetWizardFilters,
        (state): CrmEnrichmentState => ({
            ...state,
            wizardFilters: INITIAL_WIZARD_FILTERS,
        }),
    )
    .handleAction(
        ac.initializeFiltersFromJob,
        (state, { payload: { filters, isApplyFiltersSelected } }): CrmEnrichmentState => ({
            ...state,
            wizardFilters: {
                filters,
                isApplyFiltersSelected,
            },
        }),
    )
    .handleAction(
        ac.setMappings,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            mappings: payload,
        }),
    )
    .handleAction(
        ac.addMapping,
        (state, { payload }): CrmEnrichmentState => ({
            ...state,
            mappings: [...state.mappings, payload],
        }),
    )
    .handleAction(
        ac.removeMapping,
        (state, { payload: index }): CrmEnrichmentState => ({
            ...state,
            mappings: state.mappings.filter((_, i) => i !== index),
        }),
    )
    .handleAction(
        ac.updateMapping,
        (state, { payload: { index, changes } }): CrmEnrichmentState => ({
            ...state,
            mappings: state.mappings.map((m, i) => (i === index ? { ...m, ...changes } : m)),
        }),
    );

export default crmEnrichmentReducer;
